import { toFirstLowerCase, toFirstUpperCase } from 'infrastructure/functions';
import { reasonForCallObj } from 'infrastructure/consts/device-troubleshooting-modal';
import { capitalize } from 'utils';

import { deleteUnnecessaryEmptyLines } from '../helpers';

import type { IAppendixCValues } from '../types';

type TData = {
  whichPerson: string;
  otherPerson: string;
  isPatientCompleteRpmSMSForm?: string;
  additionalEducationalMaterialsSent?: string;
  deliveryMethod?: string;
  eduAdditionalDetails?: string;
  accessToReading?: string;
  isPatientHaveExpEtc?: string;
  accessToReadingHealtcare?: string;
  accessToReadingHealtcareCheckbox?: string[];
  accessToReadingHealtcareFreeText?: string;
  medicationChangesFreeText?: string;
  medicationChangesQuestionsCheckbox?: string[];
  recentUpcomingAppointmentsDate?: string;
  recentUpcomingAppointmentsTime?: string;
  recentUpcomingAppointmentsProvider?: string;
  recentUpcomingAppointmentsText?: string;
  recentUpcomingAppointments?: string;
  recentUpcomingAppointmentsMethod?: string;
  readingQuestionsRadio?: string;
  readingQuestionsText?: string;
  patientGuid: string;
  educationProvided?: string[];
};

const joinArray = (arr: string[]) => arr?.join(',\n');

export const generateMonthlyInteractionCallMessage = <T extends TData>(
  data: T,
) => {
  const {
    whichPerson,
    otherPerson,
    isPatientCompleteRpmSMSForm,
    additionalEducationalMaterialsSent,
    deliveryMethod,
    accessToReading,
    eduAdditionalDetails,
    isPatientHaveExpEtc,
    accessToReadingHealtcare,
    accessToReadingHealtcareCheckbox,
    accessToReadingHealtcareFreeText,
    medicationChangesQuestionsCheckbox,
    recentUpcomingAppointmentsText,
    recentUpcomingAppointmentsProvider,
    recentUpcomingAppointmentsDate,
    recentUpcomingAppointmentsTime,
    recentUpcomingAppointmentsMethod,
    recentUpcomingAppointments,
    readingQuestionsRadio,
    readingQuestionsText,
    patientGuid,
    educationProvided,
    medicationChangesFreeText,
  } = data;
  let message = '';
  const isPatient = whichPerson === 'Patient';
  const target =
    whichPerson === 'Other'
      ? toFirstLowerCase(otherPerson as string)
      : toFirstLowerCase(whichPerson as string);

  const speakWith = isPatient ? 'patient' : `${target}`;

  const deliveryMethods: { [key: string]: string } = {
    usmail: 'US mail',
    email: 'Email',
  };

  const speakWithUpperCase = isPatient
    ? 'Patient'
    : `${toFirstUpperCase(target)}`;

  if (isPatientCompleteRpmSMSForm !== null) {
    message += `${speakWithUpperCase} did ${
      isPatientCompleteRpmSMSForm === 'yes' ? '' : 'not '
    }complete monthly follow-up SMS.`;
  }
  if (isPatientHaveExpEtc) {
    message += `\n${speakWithUpperCase} ${
      isPatientHaveExpEtc === 'yes' ? 'confirms' : 'denies'
    } health concerns or symptoms.`;
  }

  if (readingQuestionsRadio) {
    message += `\n${speakWithUpperCase} does ${
      readingQuestionsRadio === 'yes' ? '' : 'not'
    } have additional questions about patient's readings. `;
    if (readingQuestionsText) message += `${readingQuestionsText}.`;
  }
  if (recentUpcomingAppointments) {
    message += `\n${speakWithUpperCase} ${
      recentUpcomingAppointments === 'yes' ? 'confirms' : 'denies'
    } recent or upcoming appointments. `;
    if (recentUpcomingAppointments === 'yes') {
      if (
        recentUpcomingAppointmentsProvider &&
        recentUpcomingAppointmentsDate &&
        recentUpcomingAppointmentsTime
      ) {
        let appointmentMethod = capitalize(recentUpcomingAppointmentsMethod);
        if (recentUpcomingAppointmentsMethod === 'phone_or_video_conference') {
          appointmentMethod = 'Phone/Video Conference';
        }
        message += `Appointment with ${recentUpcomingAppointmentsProvider} on ${new Date(
          recentUpcomingAppointmentsDate,
        )?.toLocaleDateString()} at ${recentUpcomingAppointmentsTime} by ${appointmentMethod}. `;
      }
      if (recentUpcomingAppointmentsText)
        message += `${recentUpcomingAppointmentsText}.`;
    }
  }

  const getAppendixCValues = (): string[] => {
    const storedValue = localStorage.getItem(
      `device-troubleshoot_appendixC_${patientGuid}`,
    );
    if (storedValue) {
      const parsedValue = JSON.parse(storedValue);
      const appendixCValues: IAppendixCValues = parsedValue;
      const { reasonForCall } = appendixCValues;
      return reasonForCall;
    }
    return [];
  };

  if (medicationChangesQuestionsCheckbox?.length) {
    message += `\nPatient has the following needs regarding their medication(s):\n`;

    if (medicationChangesQuestionsCheckbox.length) {
      message += `${joinArray(medicationChangesQuestionsCheckbox)}.`;
      const reasonForCall = getAppendixCValues();
      if (reasonForCall.includes(reasonForCallObj.monthlyInteractionCall)) {
        message +=
          '\nPatients having issues or concerns about their medications should be escalated to a nursing team.';
      }
      if (medicationChangesFreeText) {
        message += `\n${medicationChangesFreeText}.`;
      }
    }
  }

  if (accessToReadingHealtcare) {
    message += `\n${speakWithUpperCase} ${
      accessToReadingHealtcare === 'yes' ? 'confirms' : 'denies'
    } challenges with healthcare access.\n${
      accessToReadingHealtcare === 'yes'
        ? 'Patient would like assistance with the following healthcare access issues:'
        : ''
    }`;

    if (
      accessToReadingHealtcare === 'yes' &&
      accessToReadingHealtcareCheckbox?.length
    ) {
      message += `\n${joinArray(accessToReadingHealtcareCheckbox)}.`;
    }
    if (accessToReadingHealtcareFreeText) {
      message += `\n${accessToReadingHealtcareFreeText}.`;
    }
  }

  if (accessToReading) {
    message += `\nPatient would ${
      accessToReading === 'no' ? 'not' : ''
    } like access to their readings. ${
      accessToReading === 'yes'
        ? `MA assisted patient with application/online portal set-up.`
        : ''
    }`;
  }

  if (educationProvided?.length) {
    message += `\nMA provided education to ${speakWith}:`;
    message += `\n${joinArray(educationProvided)}.`;
    if (eduAdditionalDetails) {
      message += `\n${eduAdditionalDetails}.`;
    }
  }

  if (additionalEducationalMaterialsSent === 'yes' && deliveryMethod) {
    message += `\nMA reviewed and sent educational materials ${deliveryMethods[deliveryMethod]}.`;
  }

  message = deleteUnnecessaryEmptyLines(message);
  return message;
};
